export interface CandidateSchool {
  id: string;
  name: string;
}

export interface CandidateProfile {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phoneNumber: string | null;
  vehicleType: string;
  status: string;
  hasPassword?: boolean;
  school: CandidateSchool | null;
  /** Réglages d'accès de l'auto-école appliqués à l'espace élève. */
  access?: {
    canViewPayments: boolean;
    canCancelLessons: boolean;
    canBookHolidays: boolean;
    /** Réservation limitée au moniteur référent : son nom tel que l'élève le voit, sinon `null`. */
    bookingReferent?: string | null;
  };
}

export interface SessionResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  candidate: CandidateProfile;
}

export type VerifyResponse = SessionResponse;

export interface CandidateSlot {
  id: string;
  startsAt: string;
  endsAt: string;
  instructorName: string | null;
  /** Photo du moniteur, `null` sans photo (initiales). */
  instructorPhotoUrl?: string | null;
}

export interface CandidateLesson {
  id: string;
  startsAt: string;
  endsAt: string;
  status: string;
  lessonType: string | null;
  instructorName: string | null;
  /** Photo du moniteur, `null` sans photo (initiales). */
  instructorPhotoUrl?: string | null;
  bookedOnline: boolean;
  /** Jour où la leçon a été posée (fenêtre « Nouvelle leçon programmée », J25). Absent d'un serveur plus ancien. */
  plannedAt?: string | null;
  /**
   * Honorée : présence émargée ; pour une évaluation de départ, un rendez-vous, un bilan…
   * (présence présumée), aucune absence signalée une heure après la fin.
   */
  honored: boolean;
  attendanceSignedAt: string | null;
  /** Évaluation de départ, rendez-vous, bilan… : pas d'émargement. Absent d'un serveur plus ancien. */
  presumedPresence?: boolean;
  /** Absence signalée par l'auto-école (présence présumée). */
  absenceReportedAt?: string | null;
}

export interface CandidatePayment {
  id: string;
  date: string;
  label: string;
  type: string;
  amount: number;
  /** Facture réglée par ce paiement (si le libellé y renvoie), pour le PDF. */
  invoiceId?: string | null;
}

/** État de règlement calculé d'une facture émise (F39) : le statut enregistré ne change pas. */
export interface InvoiceSettlement {
  state: 'paid' | 'partial' | 'due' | 'credited';
  outstandingCents: number;
  dueOn: string;
  overdue: boolean;
}

export interface CandidateInvoice {
  id: string;
  number: string;
  title: string;
  issueDate: string;
  status: string;
  totalTtc: number;
  /** Absent si l'auto-école ne montre pas les paiements. */
  settlement?: InvoiceSettlement | null;
}

/** Facture à régler : `amountDue` est le reste dû (encaissements et avoirs déduits). */
export interface PayableInvoice extends CandidateInvoice {
  amountDue: number;
}

export interface PayableInvoicesResponse {
  paymentEnabled: boolean;
  invoices: PayableInvoice[];
}

export interface CandidateContract {
  id: string;
  number: string;
  status: string;
  signedAt: string | null;
}

export interface CandidateDocuments {
  invoices: CandidateInvoice[];
  contracts: CandidateContract[];
}

// ─── Vente à distance : « Renoncer au contrat ici » (T17) ───────────────────
/** Document vendu : contrat, devis ou achat en ligne. */
export type SaleSubjectKind = 'contract' | 'quote' | 'shop_purchase';
/** Rétractation encore possible, enregistrée, ou délai terminé (vu au jour de l'auto-école). */
export type WithdrawalState = 'open' | 'withdrawn' | 'closed';

/** Valeurs déclarées dans le formulaire de rétractation (modifiables, proposées par le serveur). */
export interface DeclaredWithdrawal {
  name: string;
  contract: string;
  email: string;
}

/** Accusé de la rétractation : les valeurs ENREGISTRÉES par le serveur, jamais celles du formulaire. */
export interface WithdrawalAck {
  /** Jour de réception, AAAA-MM-JJ (jour de l'auto-école). */
  receivedOn: string;
  /** « 14 h 05 », heure de l'auto-école ; `null` pour une rétractation reçue au bureau. */
  receivedTime: string | null;
  declaredName: string;
  declaredContract: string;
  declaredEmail: string | null;
  /** Dernier jour du remboursement, AAAA-MM-JJ. */
  refundBy: string;
}

/** Vente conclue à distance de l'élève connecté (`GET /me/withdrawals`). */
export interface CandidateWithdrawal {
  /** Identifiant de la conclusion. */
  id: string;
  subject: { kind: SaleSubjectKind; id: string; number: string };
  /** Jour de la conclusion, AAAA-MM-JJ. */
  concludedOn: string;
  /** Dernier jour (inclus) du délai de rétractation, AAAA-MM-JJ. */
  until: string;
  state: WithdrawalState;
  defaults: DeclaredWithdrawal;
  withdrawal: WithdrawalAck | null;
}

/** Page publique du lien de l'e-mail (`/renoncer`, sans compte). */
export interface PublicWithdrawal {
  state: WithdrawalState;
  school: string;
  subject: { kind: SaleSubjectKind; number: string };
  concludedOn: string;
  until: string;
  /** Titulaire du lien : « Aller à mon espace élève » pour l'élève seulement. */
  signerRole: 'student' | 'guardian';
  /** Valeurs proposées seulement pendant le délai ouvert ; `null` sinon (aucune donnée personnelle hors délai). */
  defaults: DeclaredWithdrawal | null;
  /** Rétractation enregistrée, sans les valeurs déclarées. */
  withdrawal: { receivedOn: string; receivedTime: string | null; refundBy: string } | null;
  /** Seulement dans la réponse de la confirmation : l'accusé de la rétractation enregistrée. */
  ack?: WithdrawalAck;
}

/** Devis ou commande envoyé en signature électronique par l'auto-école. */
export interface CandidateSignatureDocument {
  requestId: string;
  kind: 'quote' | 'order';
  documentName: string;
  expiresAt: string;
  /** L'élève a déjà signé ; une autre signature est attendue. */
  signed: boolean;
}

/** Défi à vérifier sur la page de signature après envoi du code par e-mail. */
export interface SignatureChallenge {
  challengeId: string;
  email: string;
  documentKind: string;
}

export type DocumentRequestStatus =
  | 'requested'
  | 'provided'
  | 'validated'
  | 'rejected'
  | 'cancelled';

export interface CandidateDocumentRequest {
  id: string;
  docType: string;
  label: string;
  status: DocumentRequestStatus;
  note: string | null;
  requestedAt: string;
  providedAt: string | null;
  hasFile: boolean;
  fileName: string | null;
  /** Motif d'une pièce refusée par l'auto-école ; sinon `null`. */
  rejectionReason?: string | null;
}

/** Pièce qui attend l'élève : demandée et pas encore envoyée, ou refusée et à renvoyer. */
export const awaitsStudent = (request: { status: string }) =>
  request.status === 'requested' || request.status === 'rejected';

export interface LivretSection {
  id: string;
  type: string;
  status: string;
}

export interface LivretLesson {
  id: string;
  type: string | null;
  startsAt: string;
  durationMinutes: number;
}

export interface CandidateLivret {
  sections: LivretSection[];
  lessons: LivretLesson[];
}

export interface CompetencyProgress {
  label: string;
  level: string;
  percent: number;
}

export interface CandidateProgress {
  competencies: CompetencyProgress[];
  readyForExam: boolean;
  latestRating: number | null;
}

export interface CandidateExam {
  date: string;
  center: string | null;
  status: string | null;
}

export interface CandidateBilan {
  id: string;
  date: string;
  instructorName: string | null;
  /** Photo du moniteur, `null` sans photo (initiales). */
  instructorPhotoUrl?: string | null;
  instructorPhone?: string | null;
  instructorEmail?: string | null;
  summary: string;
  rating: number | null;
  readyForExam: boolean;
}

// ─── Livret d'apprentissage (REMC) ──────────────────────────────────────────
export type LearningProgression = 'aborde' | 'traite' | 'assimile';

export interface LearningBookletItem {
  code: string;
  label: string;
  progression: LearningProgression | null;
  selfAssessment: number | null;
}
export interface LearningBookletCategory {
  code: string;
  title: string;
  progressPct: number;
  items: LearningBookletItem[];
}
export interface LearningBooklet {
  family: 'A' | 'B';
  referentialLabel: string;
  licenseCategory: string | null;
  overallPct: number;
  categories: LearningBookletCategory[];
}

// ─── Évaluation de départ ────────────────────────────────────────────────────
export type StartingEvalQuestionType = 'single' | 'multi' | 'boolean_quiz' | 'text' | 'date' | 'quiz' | 'order';
export interface StartingEvalOption { value: string; label: string; }
export interface StartingEvalQuestion {
  code: string;
  label: string;
  type: StartingEvalQuestionType;
  options?: StartingEvalOption[];
  optional?: boolean;
  hint?: string;
  placeholder?: string;
  /** Étapes à mémoriser pendant le chrono, avant de répondre. */
  memorize?: { title: string; text?: string; steps: string[]; seconds: number };
}
export interface StartingEvalSection {
  code: string;
  title: string;
  description?: string;
  scored?: boolean;
  memory?: boolean;
  /** `steps` : une question par écran. */
  layout?: 'page' | 'steps';
  questions: StartingEvalQuestion[];
}
export interface StartingEvalDefinition {
  version: string;
  knowledgeMax: number;
  sections: StartingEvalSection[];
}
export type StartingEvalStatus = 'not_requested' | 'pending' | 'completed';
export interface StartingEvaluation {
  status: StartingEvalStatus;
  requestedAt: string | null;
  completedAt: string | null;
  estimatedHours?: number | null;
  /** Fourchette montrée à l'élève (J7) : `estimatedHours` en est le haut. Absente d'un serveur plus ancien. */
  estimatedRange?: { from: number; to: number } | null;
  knowledgeScore: number | null;
  knowledgeMax: number | null;
  /** Proposition de l'enseignant, une fois sa partie renseignée. */
  final?: { proposedTotalHours: number; proposedPracticeHours: number } | null;
  definition: StartingEvalDefinition;
  answers: Record<string, string | string[]>;
  /** Réponses reprises de la fiche (date de naissance), proposées tant que l'évaluation est à remplir. */
  prefill?: Record<string, string>;
}
